import angular from 'angular';
import csmTools from '../tools/tools';

export default angular
  .module('csm.util.RowUpdateManager', [csmTools])

  /**
   * @ngdoc    object
   * @module   csm.util
   * @name     csm.util.RowUpdateManager.type:RowUpdateManager
   * @requires csm.util.tools
   *
   * @constructor
   * @param {string|object}   table       DOM/JQLite table object reference or selector string
   * @param {number}          columnSpan  Number of columns the update row cell will span
   * @param {string}          templateUrl Update content template url string
   * @param {string|function} controller  Registered controller name or function to associate with
   *                                      the update template content
   *
   * @description
   * This factory returns a constructor function that when instantiated will allow
   * for the adding/removal of a tableRow element to an existing table to perform
   * content updating.
   *
   * This will expect the template to come from a url and a seperate controller to
   * be used for the content.
   *
   * Only one instance of the content row will be shown at a time such that when
   * another content section is requested, the current one will be removed.
   *
   * The content controller will have the following local properties available for injection:
   *   - **`$scope`**   - Content Scope
   *   - **`$element`** - Element reference
   *   - **`$data`**    - Data pass through
   *   - **`$cancel`**  - function(rejectData) Call to end update and reject promise with reject data
   *   - **`$finish`**  - function(resolveData) Call to end update and resolve promise with resolve data
   *
   * ## Example:
   * ### HTML
   *     <table id="mytable">
   *       <thead>...</thead>
   *       <tbody>
   *         ...Content...
   *       </tbody>
   *     </table>
   *
   * ### JS
   *     var manager = new RowUpdateManager("#mytable", 4, "template.html", ContentController);
   *     $scope.onRowClick = function(event, data)
   *       {
   *       manager.load(event.currentTarget, data)
   *         .then(function(updates)
   *           {
   *           ...
   *           }, function() { console.log('cancelled'); });
   *       };
   */
  /* @example
  <example module="userImageExample">
    <file name="index.html">
      <div ng-controller="userImageController">
        <form novalidate class="simple-form">
          <label>height:</label> <input type="number" ng-model="height" /><br />
          <label>width:</label>  <input type="number" ng-model="width" /><br />
          <label>url:</label>    <input type="string" ng-model="newurl" style="width: 400px" />
        </form>
        <div style="color:red" ng-show="!imageLoaded">No image loaded.</div>
        <user-image height="{{height}}" width="{{width}}" src="{{url}}" style="border: 1px solid black"
                    on-change="onChange"></user-image>
      </div>
    </file>
    <file name="script.js">
      angular.module('userImageExample', [ 'csm.ui.userImage' ]).
      controller("userImageController", function ($scope, $sce) 
        {
        $scope.height   = 250;
        $scope.width    = 250;
        $scope.onChange = function(loaded) { $scope.imageLoaded = loaded; };
        $scope.$watch('newurl', function(newurl) 
          { 
          $scope.url = (newurl ? $sce.trustAsResourceUrl(newurl) : newurl); 
          });
        });
    </file>
  </example>
*
*******************************************************************************/
  /* @ngInject */
  .factory(
    'RowUpdateManager',
    function ($controller, $compile, $q, $rootScope, $timeout, Tools) {
      /*****************************************************************************
       * Constructor Function
       *****************************************************************************/
      function RowUpdateManager(table, columnCount, template, controller) {
        /** Instance Reference. */ var mThis = this;
        /** Table Body Reference. */ var mTableBody = null;
        /** Settings for currently display update section. */ var mCurrent =
          null;

        /** Row Template. */
        var mTemplate =
          '<tr class="updateRow"><td colspan="{0}"><div onload="centerContent()">{1}</div></td></tr>'.format(
            columnCount,
            template
          );

        /***************************************************************************
         * cleanup */
        /**
         * @ngdoc    function
         * @name     cleanup
         * @methodOf csm.util.RowUpdateManager.type:RowUpdateManager
         *
         * @description
         * Cleans up the current update content by destroying the created scope and
         * removing the update row from the DOM if it exists.
         ***************************************************************************/
        mThis.cleanup = function () {
          if (!mCurrent) return;

          tableBody()[0].removeChild(mCurrent.updateRow[0]);
          mCurrent.scope.$destroy();
          mCurrent = null;
        };

        /*****************************************************************************
         * getTableBody */
        /**
         * Attempts to find the tbody element from the provided table input and return
         * it as a JQLite object.
         *
         * @param  table DOM/JQLite table object reference or selector string
         * @return       JQLite wrapped element for the tbody element of the table param
         *****************************************************************************/
        function getTableBody(table) {
          /*---------------------------------------------------------*/
          /* Load the table body reference from the table parameter. */
          /* If the table is a selector string, then lookup the DOM  */
          /* element before getting the tbody child element          */
          /*---------------------------------------------------------*/
          var tbody = angular
            .element(
              angular.isString(table) ? document.querySelector(table) : table
            )
            .find('tbody');

          /*------------------------------------------------------------------*/
          /* Verify that the table body element has been found by checking    */
          /* the keys of the jqlite object. The keys method should return an  */
          /* array with at least one property when the element has been found */
          /*------------------------------------------------------------------*/
          if (!Object.keys(tbody).length)
            throw new Error('Table Body not found');

          return tbody;
        }

        /*******************************************************************************
         * load */
        /**
         * Ensures that only one update section is displayed. This will create and add
         * a new row element containing the update template under the provided row. If
         * no row is provided, the update section will be added to the end of the table.
         *
         * @param  element DOM table element to position the section underneath. If this
         *                 is not a tr element, then a parent traversal is done until a parent
         *                 tr element is found
         * @param  data    Data to inject into the update controller to be referenced as $data
         * @return         Promise that will be resolved with the modified contact or
         *                 rejected on a cancel
         *******************************************************************************/
        mThis.load = function (element, data) {
          /*-----------------------------*/
          /* Remove the current section. */
          /*-----------------------------*/
          mThis.cleanup();

          /*---------------------------------------------------*/
          /* Create a defer that the caller can listen against */
          /*---------------------------------------------------*/
          var defer = $q.defer();

          /** Cancel callback method. */
          var cancel = function (reject) {
            mThis.cleanup();
            defer.reject(reject);
          };

          /** Commit/finish callback method. */
          var finish = function (resolve) {
            mThis.cleanup();
            defer.resolve(resolve);
          };

          /*---------------------------------*/
          /* Set mCurrent to contain the new */
          /* scope and update JQLite object  */
          /*---------------------------------*/
          mCurrent = { scope: $rootScope.$new(true) };
          mCurrent.updateRow = angular.element(mTemplate);

          /*-------------------------------------------------*/
          /* Add function to update scope so the content can */
          /* be centered after the template has been loaded  */
          /*-------------------------------------------------*/
          mCurrent.scope.centerContent = function () {
            $timeout(function () {
              mCurrent.updateRow[0].scrollIntoView(true);
            });
          };

          /*------------------------------------------------------*/
          /* Ensure a tr element for sibling placement. If none   */
          /* is available, the row will be added as the last row. */
          /*------------------------------------------------------*/
          var siblingRow = element;
          while (siblingRow && siblingRow.tagName != 'TR')
            siblingRow = siblingRow.parentElement;

          tableBody()[0].insertBefore(
            mCurrent.updateRow[0],
            siblingRow ? siblingRow.nextSibling : null
          );

          /*----------------------------------------------------*/
          /* Instantiate the controller and compile the content */
          /*----------------------------------------------------*/
          $controller(controller, {
            $scope: mCurrent.scope, // Content Scope
            $element: mCurrent.updateRow, // Element reference
            $data: data, // Data pass through
            $cancel: cancel, // Cancel method
            $finish: finish // Finish method
          });
          $compile(mCurrent.updateRow)(mCurrent.scope);

          return defer.promise;
        };

        /***************************************************************************
         * tableBody */
        /**
         * Accessor for the table body. This will set the reference if not set. This
         * is needed as the DOM may not be fully loaded when this object is instantiated.
         ***************************************************************************/
        function tableBody() {
          if (mTableBody) return mTableBody;
          else return (mTableBody = getTableBody(table));
        }
      }

      return RowUpdateManager;
    }
  ).name;
